// BEAD-flavored project dashboard. Replaces the generic placeholder.
// First thing Clay/Brad/Joe see when they open the demo.
//
// Layout:
//   - Header: project picker + context bar
//   - 4 hero KPIs: project value · BSLs · build progress · compliance health
//   - 2-col mid: Active Phases (left) | Next Obligations 30d (right)
//   - 2-col bottom: Current Draw Period (left) | System Integrations (right)
//
// Portfolio mode: when projectId === ALL_PROJECTS, the dashboard rolls up
// across every the operator-prime award. KPIs sum across projects; phase &
// obligation lists fetch from /api/bead-pm/portfolio/* endpoints.

import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useOnboardingSteps } from './GettingStartedPage';

const ONBOARDING_DISMISS_KEY = 'mb.onboarding.banner.dismissed';

// Sentinel project ID for the cross-project rollup. Anything starting
// with __ is treated as "not a real project UUID" everywhere downstream.
const ALL_PROJECTS = '__ALL__';

const PHASE_COLOR: Record<string, string> = {
  preconstruction: '#3b82f6',
  procurement:     '#f59e0b',
  construction:    '#10b981',
  commissioning:   '#8b5cf6',
  launch:          '#ec4899',
  ongoing:         '#6b7280',
};

const STATUS_BADGE: Record<string, { bg: string; fg: string; label: string }> = {
  pending:     { bg: '#f1f5f9', fg: '#475569', label: 'Pending' },
  in_progress: { bg: '#fef3c7', fg: '#92400e', label: 'In progress' },
  submitted:   { bg: '#dbeafe', fg: '#1e40af', label: 'Submitted' },
  accepted:    { bg: '#d1fae5', fg: '#065f46', label: 'Accepted' },
  active:      { bg: '#d1fae5', fg: '#065f46', label: 'Active' },
  planned:     { bg: '#f1f5f9', fg: '#475569', label: 'Planned' },
  rejected:    { bg: '#fee2e2', fg: '#991b1b', label: 'Rejected' },
  overdue:     { bg: '#fecaca', fg: '#7f1d1d', label: 'Overdue' },
  na:          { bg: '#e5e7eb', fg: '#6b7280', label: 'N/A' },
};

function StatusBadge({ status }: { status: string }) {
  const s = STATUS_BADGE[status] ?? STATUS_BADGE.pending;
  return (
    <span style={{
      background: s.bg, color: s.fg,
      padding: '2px 8px', borderRadius: 999,
      fontSize: 10, fontWeight: 700,
      textTransform: 'uppercase', letterSpacing: '.04em',
      whiteSpace: 'nowrap',
    }}>{s.label}</span>
  );
}

function fmtUSD(n: number, abbreviate = true): string {
  const num = Number(n) || 0;
  if (abbreviate) {
    if (num >= 1_000_000) return '$' + (num / 1_000_000).toFixed(1) + 'M';
    if (num >= 1_000)     return '$' + (num / 1_000).toFixed(0) + 'K';
  }
  return '$' + Math.round(num).toLocaleString();
}

// Parse YYYY-MM-DD as a LOCAL date (not UTC). Avoids the off-by-one
// timezone bug where new Date("2026-04-01") in PT renders as March 31.
function parseLocalDate(s: string | null | undefined): Date | null {
  if (!s) return null;
  const m = String(s).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return new Date(+m[1], +m[2] - 1, +m[3]);
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
}

function daysFromNow(d: string): number {
  const dt = parseLocalDate(d);
  if (!dt) return NaN;
  const ms = dt.getTime() - new Date().setHours(0, 0, 0, 0);
  return Math.round(ms / 86_400_000);
}

interface HeroKPIProps { label: string; value: string; sub?: string; accent?: string; link?: string }
function HeroKPI({ label, value, sub, accent, link }: HeroKPIProps) {
  const navigate = useNavigate();
  return (
    <div
      className="card"
      onClick={link ? () => navigate(link) : undefined}
      style={{
        borderTop: accent ? `3px solid ${accent}` : undefined,
        height: '100%',
        cursor: link ? 'pointer' : 'default',
      }}>
      <div className="card__body" style={{ padding: 18 }}>
        <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.06em', fontWeight: 600 }}>{label}</div>
        <div style={{ fontSize: 28, fontWeight: 800, marginTop: 6, color: '#0f172a', lineHeight: 1.1 }}>{value}</div>
        {sub && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>{sub}</div>}
      </div>
    </div>
  );
}

// Compact KPI used inside the Permits dashboard card. Smaller than the hero
// KPIs at the top of the page — sized for an in-card 4-up grid.
function DashKpi({ label, value, tone, sub }: { label: string; value: any; tone?: 'good' | 'warn' | 'bad'; sub?: string }) {
  const color =
    tone === 'good' ? '#10b981' :
    tone === 'warn' ? '#f59e0b' :
    tone === 'bad'  ? '#ef4444' : '#0f172a';
  return (
    <div>
      <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.05em', fontWeight: 700 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 800, color, marginTop: 2, lineHeight: 1.1 }}>{value}</div>
      {sub && <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 1 }}>{sub}</div>}
    </div>
  );
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const [projectId, setProjectId] = useState<string>(ALL_PROJECTS);

  const isPortfolio = projectId === ALL_PROJECTS;

  // First-run onboarding nudge. Reads the same completeness as the Getting
  // Started page; only shows while setup is incomplete and not dismissed.
  const onboarding = useOnboardingSteps();
  const [obDismissed, setObDismissed] = useState<boolean>(() => {
    try { return localStorage.getItem(ONBOARDING_DISMISS_KEY) === '1'; } catch { return false; }
  });
  const showOnboarding = !onboarding.loading && onboarding.doneCount < onboarding.total && !obDismissed;
  const dismissOnboarding = () => {
    try { localStorage.setItem(ONBOARDING_DISMISS_KEY, '1'); } catch {}
    setObDismissed(true);
  };

  const { data: projects = [] } = useQuery<any[]>({
    queryKey: ['bead-projects-dashboard'],
    queryFn: () => api.get('/bead/projects'),
  });
  const project = projects.find((p: any) => p.id === projectId);

  // the operator-only roster for the portfolio rollup
  const portfolioProjects = useMemo(
    () => projects,
    [projects]
  );

  // ─── Single-project queries (only run when a real project is selected) ───
  const { data: phasesData } = useQuery<{ phases: any[]; tasks: any[] }>({
    queryKey: ['pm-phases', projectId],
    queryFn: () => api.get(`/bead-pm/projects/${projectId}/phases`),
    enabled: !!projectId && !isPortfolio,
  });

  const { data: pmSummary } = useQuery<any>({
    queryKey: ['pm-summary', projectId],
    queryFn: () => api.get(`/bead-pm/projects/${projectId}/pm-summary`),
    enabled: !!projectId && !isPortfolio,
  });

  const { data: complianceSummary } = useQuery<any>({
    queryKey: ['comp-summary', projectId],
    queryFn: () => api.get(`/bead-pm/projects/${projectId}/compliance-summary`),
    enabled: !!projectId && !isPortfolio,
  });

  // Permits-side summary so the dashboard can flag when permit work is the
  // blocker — distinct from obligation paperwork. Portfolio mode skips this
  // for now (would need a per-project rollup endpoint).
  const { data: permitSummary } = useQuery<any>({
    queryKey: ['dash-permit-summary', projectId],
    queryFn: () => api.get(`/permits/summary?project_id=${projectId}`),
    enabled: !!projectId && !isPortfolio,
  });

  const today = useMemo(() => new Date(), []);
  const todayISO = today.toISOString().slice(0, 10);
  const futureISO = new Date(today.getTime() + 30 * 86_400_000).toISOString().slice(0, 10);

  const { data: upcomingObligations = [] } = useQuery<any[]>({
    queryKey: ['upcoming-obligations', projectId],
    queryFn: () => api.get(`/bead-pm/projects/${projectId}/obligations?from=${todayISO}&to=${futureISO}`),
    enabled: !!projectId && !isPortfolio,
  });

  const { data: allObligations = [] } = useQuery<any[]>({
    queryKey: ['all-obligations', projectId],
    queryFn: () => api.get(`/bead-pm/projects/${projectId}/obligations`),
    enabled: !!projectId && !isPortfolio,
  });

  // ─── Portfolio queries (only run in __ALL__ mode) ───
  const { data: portfolioSummary } = useQuery<any>({
    queryKey: ['portfolio-summary'],
    queryFn: () => api.get('/bead-pm/portfolio/summary'),
    enabled: isPortfolio,
  });

  const { data: portfolioPhases = [] } = useQuery<any[]>({
    queryKey: ['portfolio-phases'],
    queryFn: () => api.get('/bead-pm/portfolio/active-phases'),
    enabled: isPortfolio,
  });

  const { data: portfolioObligations = [] } = useQuery<any[]>({
    queryKey: ['portfolio-obligations', todayISO, futureISO],
    queryFn: () => api.get(`/bead-pm/portfolio/obligations?from=${todayISO}&to=${futureISO}`),
    enabled: isPortfolio,
  });

  const { data: qboAccounts = [] } = useQuery<any[]>({
    queryKey: ['qbo-accounts'],
    queryFn: () => api.get('/bead-pm/qbo-accounts'),
  });

  const { data: integrationsRaw } = useQuery<{ tiles: any[] }>({
    queryKey: ['integrations-status'],
    queryFn: () => api.get('/integrations/status'),
  });
  const integrations = integrationsRaw?.tiles ?? [];

  // ─── Derived KPIs (branch on portfolio vs single) ───
  const activePhases = isPortfolio
    ? portfolioPhases
    : (phasesData?.phases ?? []).filter((p: any) => p.status === 'active');

  const buildProgress = isPortfolio
    ? Math.round(Number(portfolioSummary?.pm?.avg_pct_complete ?? 0))
    : (phasesData?.phases?.length ?? 0) > 0
      ? Math.round(phasesData!.phases.reduce((s: number, p: any) => s + (p.pct_complete || 0), 0) / phasesData!.phases.length)
      : 0;

  // Financials — single project pulls from project record; portfolio sums
  const grantUSD = isPortfolio
    ? Number(portfolioSummary?.federal_share_usd ?? 0)
    : Number(project?.federal_share_usd ?? project?.grant_amount_usd ?? 0);
  const totalUSD = isPortfolio
    ? Number(portfolioSummary?.total_budget_usd ?? 0)
    : Number(project?.total_budget_usd ?? project?.total_project_cost_usd ?? 0);
  const matchUSD = isPortfolio
    ? Number(portfolioSummary?.match_share_usd ?? 0)
    : Number(project?.match_share_usd ?? project?.match_amount_usd ?? Math.max(0, totalUSD - grantUSD));
  const fedPct   = totalUSD > 0 ? (grantUSD / totalUSD * 100).toFixed(1) : '—';

  const totalBSLs = isPortfolio
    ? Number(portfolioSummary?.total_bsls ?? 0)
    : Number(project?.awarded_bsl_count ?? 0);

  const acceptedYTD = isPortfolio
    ? Number(portfolioSummary?.compliance?.accepted_ytd ?? 0)
    : allObligations.filter((o: any) => o.status === 'accepted').length;

  const overdueCount = isPortfolio
    ? Number(portfolioSummary?.compliance?.overdue ?? 0)
    : Number(complianceSummary?.overdue ?? 0);

  const due7dCount = isPortfolio
    ? Number(portfolioSummary?.compliance?.due_7d ?? 0)
    : Number(complianceSummary?.due_7d ?? 0);

  // Obligation summaries (only for the current draw bottom card; single-project only)
  const currentDraw = !isPortfolio ? allObligations.find((o: any) =>
    o.code === 'FED-FIN-DRAW' && (o.status === 'in_progress' || o.status === 'submitted')
  ) : null;

  const babaAccepted = !isPortfolio ? allObligations.filter((o: any) => o.code === 'FED-MAT-BABA' && o.status === 'accepted').length : 0;
  const babaPending  = !isPortfolio ? allObligations.filter((o: any) => o.code === 'FED-MAT-BABA' && (o.status === 'pending' || o.status === 'in_progress')).length : 0;
  const wh347Accepted = !isPortfolio ? allObligations.filter((o: any) => o.code === 'FED-LAB-WH347' && o.status === 'accepted').length : 0;
  const wh347Total    = !isPortfolio ? allObligations.filter((o: any) => o.code === 'FED-LAB-WH347').length : 0;

  const qboMapped = qboAccounts.filter((a: any) => a.bead_category !== 'unmapped').length;
  const qboTotal  = qboAccounts.length;

  // Header subtitle context
  const headerSubtitle = isPortfolio
    ? `Portfolio rollup · ${portfolioSummary?.project_count ?? portfolioProjects.length} awards · ${portfolioSummary?.state_count ?? '—'} states`
    : project
      ? `${project.project_code} · ${project.project_name}${project.county ? ' · ' + project.county : ''}${project.state ? ' · ' + project.state : ''}`
      : 'Loading…';

  // Build BSL subtitle
  const bslSub = isPortfolio
    ? `${portfolioProjects.length} awards · ${portfolioProjects.filter((p: any) => p.tech_type === 'fiber').length} fiber projects`
    : project?.tech_type === 'fiber' ? 'Fiber to the Premises (FTTP)' : (project?.tech_type ?? '—');

  // Build progress subtitle
  const progressSub = isPortfolio
    ? `${portfolioSummary?.pm?.phases_active ?? 0} active phases · ${portfolioSummary?.pm?.critical_path_tasks ?? 0} on critical path · across ${portfolioSummary?.project_count ?? '—'} awards`
    : `${pmSummary?.phases_active ?? 0} active of ${pmSummary?.phase_count ?? 0} phases · ${pmSummary?.critical_path_tasks ?? 0} on critical path`;

  return (
    <>
      {showOnboarding && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12,
          padding: '10px 16px', marginBottom: 16,
          background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8,
        }}>
          <span style={{ fontSize: 18 }} aria-hidden>🧭</span>
          <div style={{ flex: 1, minWidth: 0, fontSize: 13, color: '#1e3a5f' }}>
            <b>Finish setting up your instance</b>
            <span style={{ color: '#3b6098' }}>
              {' · '}{onboarding.doneCount} of {onboarding.total} steps done
            </span>
          </div>
          <Link to="/settings/getting-started" style={{
            fontSize: 13, fontWeight: 600, textDecoration: 'none',
            padding: '6px 12px', borderRadius: 6,
            background: '#0f172a', color: 'white', whiteSpace: 'nowrap',
          }}>Getting started →</Link>
          <button
            onClick={dismissOnboarding}
            title="Dismiss"
            style={{
              border: 'none', background: 'transparent', cursor: 'pointer',
              color: '#64748b', fontSize: 18, lineHeight: 1, padding: '0 4px',
            }}
          >×</button>
        </div>
      )}

      <div className="page__header">
        <div>
          <h1 className="page__title">{isPortfolio ? 'Portfolio Dashboard' : 'Project Dashboard'}</h1>
          <div className="page__subtitle">{headerSubtitle}</div>
        </div>
        <select value={projectId} onChange={e => setProjectId(e.target.value)}
          style={{ padding: '6px 10px', borderRadius: 4, border: '1px solid var(--border)', background: 'white', fontSize: 13, minWidth: 280 }}>
          <option value={ALL_PROJECTS}>★ All Awards ({portfolioProjects.length})</option>
          <option disabled>──────── Individual Awards ────────</option>
          {projects.map((p: any) => (
            <option key={p.id} value={p.id}>{p.project_code} — {p.project_name} ({p.state ?? '—'})</option>
          ))}
        </select>
      </div>

      {/* Hero KPI row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 16 }}>
        <HeroKPI
          label={isPortfolio ? 'Portfolio Federal Award' : 'Total Project Value'}
          value={fmtUSD(totalUSD)}
          sub={`${fmtUSD(grantUSD)} federal · ${fmtUSD(matchUSD)} match (${fedPct}%)`}
          accent="#0f172a"
        />
        <HeroKPI
          label={isPortfolio ? 'BSLs Across Portfolio' : 'BSLs Awarded'}
          value={Number(totalBSLs).toLocaleString()}
          sub={bslSub}
          accent="#3b82f6"
        />
        <HeroKPI
          label={isPortfolio ? 'Avg Build Progress' : 'Build Progress'}
          value={`${buildProgress}%`}
          sub={progressSub}
          accent="#10b981"
          link="/construction"
        />
        <HeroKPI
          label="Compliance Health"
          value={`${overdueCount} overdue`}
          sub={`${due7dCount} due in 7d · ${acceptedYTD} accepted YTD`}
          accent={overdueCount > 0 ? '#ef4444' : '#10b981'}
          link="/compliance"
        />
      </div>

      {/* Mid row: Active Phases | Next Obligations */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
        <div className="card">
          <div className="card__header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>Active Phases{isPortfolio ? ` · ${activePhases.length} across portfolio` : ''}</span>
            <Link to="/construction" style={{ fontSize: 12 }}>Open roadmap →</Link>
          </div>
          <div className="card__body" style={{ padding: 0 }}>
            {activePhases.length === 0 ? (
              <div style={{ padding: 16, color: 'var(--text-muted)' }}>No active phases.</div>
            ) : (
              activePhases.slice(0, 8).map((p: any) => {
                const end = parseLocalDate(p.planned_end);
                const daysLeft = end ? Math.ceil((end.getTime() - Date.now()) / 86_400_000) : null;
                const color = PHASE_COLOR[p.phase_type] ?? '#3b82f6';
                return (
                  <div key={p.id}
                    onClick={() => navigate('/construction')}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                    style={{
                      padding: '12px 16px', borderBottom: '1px solid var(--border)',
                      display: 'flex', alignItems: 'center', gap: 12,
                      borderLeft: `4px solid ${color}`,
                      cursor: 'pointer',
                      transition: 'background 0.12s',
                      background: 'white',
                    }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 600 }}>
                        {isPortfolio && p.project_code && (
                          <span style={{
                            display: 'inline-block', background: '#e0e7ff', color: '#3730a3',
                            fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 3,
                            marginRight: 6, fontFamily: 'ui-monospace, monospace',
                          }}>{p.project_code}</span>
                        )}
                        {!isPortfolio && p.template_code && <span style={{ color: '#94a3b8', marginRight: 6 }}>{p.template_code}</span>}
                        {p.name}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
                        <div style={{ flex: 1, height: 6, background: '#e2e8f0', borderRadius: 999, overflow: 'hidden' }}>
                          <div style={{ width: (p.pct_complete || 0) + '%', height: '100%', background: color, transition: 'width 0.4s ease' }}/>
                        </div>
                        <span style={{ fontSize: 11, fontWeight: 700, color: '#0f172a', minWidth: 32, textAlign: 'right' }}>{p.pct_complete || 0}%</span>
                      </div>
                    </div>
                    {end && (
                      <div style={{ textAlign: 'right', minWidth: 92 }}>
                        <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.05em', fontWeight: 700 }}>Due</div>
                        <div style={{ fontSize: 13, fontWeight: 600 }}>{end.toLocaleDateString()}</div>
                        <div style={{ fontSize: 10, color: (daysLeft ?? 0) < 0 ? '#ef4444' : (daysLeft ?? 0) < 14 ? '#f59e0b' : 'var(--text-muted)' }}>
                          {(daysLeft ?? 0) < 0 ? `${Math.abs(daysLeft!)}d overdue` : `${daysLeft}d remaining`}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>

        <div className="card">
          <div className="card__header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>Next Obligations · 30 days{isPortfolio ? ' · across portfolio' : ''}</span>
            <Link to="/compliance" style={{ fontSize: 12 }}>Open calendar →</Link>
          </div>
          <div className="card__body" style={{ padding: 0 }}>
            {(() => {
              const arr = isPortfolio
                ? (Array.isArray(portfolioObligations) ? portfolioObligations : [])
                : (Array.isArray(upcomingObligations) ? upcomingObligations : []);
              if (arr.length === 0) {
                return <div style={{ padding: 16, color: 'var(--text-muted)' }}>Nothing due in the next 30 days.</div>;
              }
              return arr.slice(0, 6).map((o: any) => {
                const days = daysFromNow(String(o.due_date ?? ''));
                const cadence = String(o.cadence_rule ?? '').replace(/_/g, ' ');
                return (
                  <div key={o.id}
                    onClick={() => navigate('/bead-compliance/calendar')}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                    style={{
                      padding: '10px 16px', borderBottom: '1px solid var(--border)',
                      display: 'flex', alignItems: 'center', gap: 10,
                      cursor: 'pointer', transition: 'background 0.12s',
                      background: 'white',
                    }}>
                    <div style={{ width: 100, fontSize: 10, fontFamily: 'ui-monospace, monospace', color: '#94a3b8' }}>{o.code}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {isPortfolio && o.project_code && (
                          <span style={{
                            display: 'inline-block', background: '#e0e7ff', color: '#3730a3',
                            fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 3,
                            marginRight: 6, fontFamily: 'ui-monospace, monospace',
                          }}>{o.project_code}</span>
                        )}
                        {o.name}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{o.category_label} · {cadence}</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: days < 0 ? '#ef4444' : days <= 7 ? '#f59e0b' : '#0f172a' }}>
                        {Number.isNaN(days) ? '—' : days < 0 ? `${Math.abs(days)}d overdue` : days === 0 ? 'today' : `in ${days}d`}
                      </div>
                      <div style={{ marginTop: 2 }}><StatusBadge status={o.status}/></div>
                    </div>
                  </div>
                );
              });
            })()}
          </div>
        </div>

        {/* Permits card — shows permit state for the active project.
            Hidden in portfolio mode (would need a rollup endpoint). */}
        {!isPortfolio && permitSummary && (
          <div className="card">
            <div className="card__header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>Permits</span>
              <Link to="/permits" style={{ fontSize: 12 }}>Open permits →</Link>
            </div>
            <div className="card__body" style={{ padding: 16 }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
                <DashKpi label="Needed"     value={permitSummary.needed ?? 0}    tone={(permitSummary.needed ?? 0) > 0 ? 'warn' : 'good'} />
                <DashKpi label="In flight"  value={(Number(permitSummary.applied ?? 0)) + (Number(permitSummary.in_review ?? 0))} />
                <DashKpi label="Issued"     value={permitSummary.issued ?? 0}    tone="good" />
                <DashKpi label="Denied"     value={permitSummary.denied ?? 0}    tone={(permitSummary.denied ?? 0) > 0 ? 'bad' : undefined} />
              </div>

              {/* Quick bucket roll-up */}
              <div style={{ marginTop: 14, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {[
                  ['environmental', '🌿 Environmental', permitSummary.bucket_environmental],
                  ['crossing',      '🚧 Crossings',     permitSummary.bucket_crossing],
                  ['row',           '🛣 ROW',           permitSummary.bucket_row],
                  ['site',          '🏗 Site',          permitSummary.bucket_site],
                  ['pole',          '⫶ Poles',          permitSummary.bucket_pole],
                ].filter(([,, n]) => Number(n ?? 0) > 0).map(([k, label, n]) => (
                  <span key={k as string} style={{
                    padding: '4px 10px', fontSize: 11, fontWeight: 600,
                    background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 999,
                  }}>{label as string}: <b>{Number(n ?? 0)}</b></span>
                ))}
              </div>

              {Number(permitSummary.expiring_30d ?? 0) > 0 && (
                <div style={{
                  marginTop: 12, padding: '8px 12px', fontSize: 12, fontWeight: 600,
                  background: '#fef3c7', border: '1px solid #fbbf24', borderRadius: 4, color: '#92400e',
                }}>
                  ⚠ {permitSummary.expiring_30d} permit{Number(permitSummary.expiring_30d) === 1 ? '' : 's'} expire in the next 30 days
                </div>
              )}

              {Number(permitSummary.total_crossings ?? 0) > 0 && (
                <div style={{ marginTop: 8, fontSize: 11, color: 'var(--text-muted)' }}>
                  {permitSummary.total_crossings} physical crossings tracked across all master permits
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Bottom row: Current Draw (or Portfolio Composition) | System Integrations */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
        {isPortfolio ? (
          <div className="card">
            <div className="card__header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>Portfolio by State</span>
              <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Pick a single award above for draw status</span>
            </div>
            <div className="card__body">
              {(() => {
                // Group the operator projects by state
                const byState: Record<string, { count: number; bsls: number; federal: number }> = {};
                for (const p of portfolioProjects) {
                  const s = p.state ?? '—';
                  if (!byState[s]) byState[s] = { count: 0, bsls: 0, federal: 0 };
                  byState[s].count += 1;
                  byState[s].bsls += Number(p.awarded_bsl_count ?? 0);
                  byState[s].federal += Number(p.federal_share_usd ?? 0);
                }
                const rows = Object.entries(byState).sort((a, b) => b[1].federal - a[1].federal);
                if (rows.length === 0) return <div style={{ color: 'var(--text-muted)' }}>No awards yet.</div>;
                return (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(' + Math.min(rows.length, 4) + ', 1fr)', gap: 12 }}>
                    {rows.map(([state, agg]) => (
                      <div key={state}
                        style={{
                          padding: 12, borderRadius: 6, border: '1px solid var(--border)',
                          background: '#f8fafc',
                        }}>
                        <div style={{ fontSize: 18, fontWeight: 800, color: '#0f172a' }}>{state}</div>
                        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{agg.count} {agg.count === 1 ? 'award' : 'awards'}</div>
                        <div style={{ marginTop: 8, fontSize: 13, fontWeight: 600 }}>{agg.bsls.toLocaleString()} BSLs</div>
                        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{fmtUSD(agg.federal)} federal</div>
                      </div>
                    ))}
                  </div>
                );
              })()}
              <div style={{ marginTop: 14, fontSize: 11, color: 'var(--text-muted)', borderTop: '1px solid var(--border)', paddingTop: 10 }}>
                {portfolioSummary?.compliance?.in_flight ?? 0} obligations in flight ·
                {' '}{portfolioSummary?.compliance?.total ?? 0} total tracked across portfolio
              </div>
            </div>
          </div>
        ) : (
          <div className="card">
            <div className="card__header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>Current Draw Period</span>
              <Link to="/bead-compliance/reimbursement" style={{ fontSize: 12 }}>Open reimbursements →</Link>
            </div>
            <div className="card__body">
              {currentDraw ? (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: 16, fontWeight: 700 }}>
                      {(() => {
                        const dt = parseLocalDate(currentDraw.period_start);
                        return dt ? dt.toLocaleString('en-US', { month: 'long', year: 'numeric' }) + ' draw' : 'Current draw';
                      })()}
                    </span>
                    <StatusBadge status={currentDraw.status} />
                  </div>
                  {currentDraw.notes && (
                    <div style={{ marginTop: 6, fontSize: 13, color: 'var(--text-muted)' }}>
                      {currentDraw.notes}
                    </div>
                  )}
                  <div style={{ marginTop: 16, display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
                    <div>
                      <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.05em', fontWeight: 700 }}>QBO Accounts mapped</div>
                      <div style={{ fontSize: 20, fontWeight: 800, marginTop: 4, color: qboMapped === qboTotal ? '#10b981' : '#0f172a' }}>{qboMapped} <span style={{ color: '#94a3b8', fontSize: 14, fontWeight: 600 }}>/ {qboTotal}</span></div>
                      <Link to="/settings/qbo-mapping" style={{ fontSize: 11 }}>Map more →</Link>
                    </div>
                    <div onClick={() => navigate('/bead-compliance/baba')}
                      style={{ cursor: 'pointer' }}>
                      <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.05em', fontWeight: 700 }}>BABA Certs</div>
                      <div style={{ fontSize: 20, fontWeight: 800, marginTop: 4, color: '#0f172a' }}>{babaAccepted} <span style={{ color: '#94a3b8', fontSize: 14, fontWeight: 600 }}>accepted</span></div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{babaPending} pending →</div>
                    </div>
                    <div onClick={() => navigate('/bead-compliance/davis-bacon')}
                      style={{ cursor: 'pointer' }}>
                      <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.05em', fontWeight: 700 }}>WH-347 Weekly</div>
                      <div style={{ fontSize: 20, fontWeight: 800, marginTop: 4, color: '#0f172a' }}>{wh347Accepted} <span style={{ color: '#94a3b8', fontSize: 14, fontWeight: 600 }}>/ {wh347Total}</span></div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>certified payrolls →</div>
                    </div>
                  </div>
                </>
              ) : (
                <div style={{ color: 'var(--text-muted)' }}>No active draw period.</div>
              )}
            </div>
          </div>
        )}

        <div className="card">
          <div className="card__header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>System Integrations</span>
            <Link to="/settings/integrations" style={{ fontSize: 12 }}>View all →</Link>
          </div>
          <div className="card__body" style={{ padding: 0 }}>
            {(() => {
              const arr = Array.isArray(integrations) ? integrations : [];
              if (arr.length === 0) {
                return <div style={{ padding: 16, color: 'var(--text-muted)' }}>Polling…</div>;
              }
              return arr.slice(0, 4).map((tile: any) => (
                <div key={tile.system ?? Math.random()}
                  onClick={() => navigate('/settings/integrations')}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                  style={{
                    padding: '12px 16px', borderBottom: '1px solid var(--border)',
                    display: 'flex', alignItems: 'center', gap: 10,
                    cursor: 'pointer', transition: 'background 0.12s',
                    background: 'white',
                  }}>
                  <span style={{
                    width: 8, height: 8, borderRadius: 999,
                    background: tile.connected ? '#10b981' : (tile.category === 'roadmap' ? '#94a3b8' : '#ef4444'),
                    flexShrink: 0,
                  }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{tile.label ?? '—'}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {String(tile.description ?? '')}
                    </div>
                  </div>
                  <div style={{ fontSize: 11, color: tile.connected ? '#10b981' : tile.category === 'roadmap' ? '#94a3b8' : '#ef4444', fontWeight: 600, flexShrink: 0 }}>
                    {tile.category === 'roadmap' ? 'Roadmap' : tile.connected ? `${tile.latency_ms ?? '—'}ms` : 'Disconnected'}
                  </div>
                </div>
              ));
            })()}
          </div>
        </div>
      </div>
    </>
  );
}

